import type {
  AutocompleteProvider,
  AutocompleteContext,
  BlockContextItem,
} from '../../types';
import type { AutocompleteOptions, ProcessedAutocompleteItem } from './types';
import { createFreeformItem, createErrorItem, createWarningItem } from './freeformItems';

/**
 * Clean the query string by removing trailing braces.
 * This allows the provider to match against the actual variable name
 * when the user is typing the closing braces.
 */
export function cleanQuery(query: string): string {
  let clean = query;
  if (clean.endsWith('}')) {
    clean = clean.slice(0, -1);
  }
  return clean;
}

/**
 * Parse the query into components for the autocomplete context.
 */
export function parseQuery(query: string): {
  fullText: string;
  currentWord: string;
  previousWords: string[];
} {
  const words = query.trim().split(/\s+/).filter(Boolean);
  const currentWord = words[words.length - 1] || '';
  const previousWords = words.slice(0, -1);

  return {
    fullText: query,
    currentWord,
    previousWords,
  };
}

/**
 * Build the autocomplete context from query and block context.
 */
export function buildContext(
  query: string,
  blockContext: BlockContextItem[],
  editor: unknown
): AutocompleteContext {
  const { fullText, currentWord, previousWords } = parseQuery(query);

  return {
    fullText,
    currentWord,
    previousWords,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor: editor as any,
    blockContext,
  };
}

/**
 * Process the provider's response into items ready for display.
 * Handles isComplete, errors, and freeform mode.
 */
export async function processProviderResponse(
  provider: AutocompleteProvider,
  query: string,
  context: AutocompleteContext,
  options: AutocompleteOptions
): Promise<ProcessedAutocompleteItem[]> {
  const cleanedQuery = cleanQuery(query);

  try {
    const response = await provider.onQuery(cleanedQuery, context);

    // Handle isComplete response - auto-select first item
    if (response.isComplete) {
      const firstItem = response.items[0];
      const completeItem: ProcessedAutocompleteItem = {
        id: '__complete__',
        label: firstItem?.label || query,
        value: firstItem?.value || query,
        description: firstItem?.description,
        isBlockType: firstItem?.isBlockType,
        blockType: firstItem?.blockType,
        nonEditable: firstItem?.nonEditable,
        isComplete: true,
      };
      return [completeItem];
    }

    // Handle freeform mode
    if (options.allowFreeformHandlebars) {
      // If provider returned valid items, show them
      if (response.items.length > 0 && !response.error) {
        return response.items.map((item) => ({
          ...item,
          isComplete: false,
          isError: false,
        }));
      }

      // If no valid items or error, create a freeform item
      if (cleanedQuery.trim()) {
        const freeformItem = createFreeformItem(cleanedQuery);
        if (response.error) {
          // Show both freeform option and warning
          return [
            freeformItem,
            createWarningItem(response.error),
          ];
        }
        return [freeformItem];
      }
    }

    // Handle error response
    if (response.error) {
      return [createErrorItem(response.error)];
    }

    // Normal response - return items
    return response.items.map((item) => ({
      ...item,
      isComplete: false,
      isError: false,
    }));
  } catch (err) {
    return [
      createErrorItem(
        err instanceof Error ? err.message : 'Failed to load suggestions'
      ),
    ];
  }
}

/**
 * Check if the query ends with closing braces (user typing }}).
 */
export function isClosingBraces(query: string, event: KeyboardEvent): boolean {
  return event.key === '}' && query.endsWith('}');
}

/**
 * Find the first selectable (non-error) item from the list.
 */
export function findSelectableItem(
  items: ProcessedAutocompleteItem[]
): ProcessedAutocompleteItem | undefined {
  return items.find((item) => !item.isError);
}

/**
 * Get the item to insert when user types }}.
 * In freeform mode, prioritizes exact user input over autocomplete suggestions.
 */
export function getOrCreateSelectableItem(
  items: ProcessedAutocompleteItem[],
  query: string,
  options: AutocompleteOptions
): ProcessedAutocompleteItem | undefined {
  const cleanedQuery = cleanQuery(query).trim();

  // In freeform mode, create item for exactly what the user typed
  if (options.allowFreeformHandlebars && cleanedQuery) {
    return createFreeformItem(cleanedQuery);
  }

  // Non-freeform mode: use first selectable item from provider
  return findSelectableItem(items);
}
